import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { ArrowRight, Check, Home, Bus, Scale } from "lucide-react";
import { PROGRAMS } from "../data/pages";

const ICONS = { Home, Bus, Scale };

const ProgramDetailPage = () => {
  const { slug } = useParams();
  const program = PROGRAMS.find((p) => p.slug === slug);
  if (!program) return <Navigate to="/programs" replace />;

  const Icon = ICONS[program.icon] || Home;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Program"
          title={program.title}
          description={program.short}
          breadcrumbs={[
            { label: "Programs", href: "/programs" },
            { label: program.title },
          ]}
          image={program.image}
        />

        {/* Intro */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center mb-6">
                <Icon className="w-6 h-6 text-[hsl(var(--rc-purple))]" />
              </div>
              <h2 className="font-serif-display text-[32px] md:text-[38px] leading-[1.15] font-semibold text-[hsl(var(--rc-deep))]">
                What this program looks like.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[16.5px] leading-[1.8] text-[hsl(var(--rc-slate))]">
                {program.intro}
              </p>
              <ul className="mt-8 space-y-3">
                {program.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--rc-gold))]/15 flex items-center justify-center shrink-0 text-[hsl(var(--rc-gold))] mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-[15px] leading-[1.65] text-[hsl(var(--rc-charcoal))]">
                      {h}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl mb-12">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                How it works
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Four steps from first contact to long-term connection.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {program.process.map((s) => (
                <div
                  key={s.step}
                  className="rc-card-hover relative bg-white border border-[hsl(var(--border))] rounded-2xl p-7"
                >
                  <div className="font-serif-display text-[44px] leading-none text-[hsl(var(--rc-purple))]/30 font-semibold">
                    {s.step}
                  </div>
                  <h3 className="font-serif-display mt-3 text-[20px] font-semibold text-[hsl(var(--rc-deep))]">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.65] text-[hsl(var(--rc-slate))]">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other programs */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-10 gap-5 flex-wrap">
              <h2 className="font-serif-display text-[28px] md:text-[34px] font-semibold text-[hsl(var(--rc-deep))]">
                Explore other programs
              </h2>
              <Link
                to="/programs"
                className="text-[14px] font-semibold text-[hsl(var(--rc-purple))] hover:text-[hsl(var(--rc-deep))]"
              >
                View all programs →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {PROGRAMS.filter((p) => p.slug !== slug).map((p) => {
                const O = ICONS[p.icon] || Home;
                return (
                  <Link
                    key={p.slug}
                    to={`/programs/${p.slug}`}
                    className="rc-card-hover group flex items-center gap-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-5 pr-7"
                  >
                    <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <O className="w-4 h-4 text-[hsl(var(--rc-purple))]" />
                        <span className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))] font-semibold">
                          Program
                        </span>
                      </div>
                      <h3 className="font-serif-display mt-1 text-[20px] font-semibold text-[hsl(var(--rc-deep))] group-hover:text-[hsl(var(--rc-purple))] transition-colors">
                        {p.title}
                      </h3>
                      <p className="mt-1 text-[13.5px] leading-[1.55] text-[hsl(var(--rc-slate))] line-clamp-2">
                        {p.short}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--rc-purple))] group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <CTABand />
      </main>
      <Footer />
    </div>
  );
};

export default ProgramDetailPage;
